import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from './ui/button';
import { Book, GraduationCap, Globe, Info, LogIn, LogOut, Sparkles, User, MessageCircle, Library, Home } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';

const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const { t } = useLanguage();
  const { user, login, logout, isAuthenticated, loading } = useAuth();

  React.useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navItems = [
    { name: 'Home', icon: Home, href: '/', isLink: true },
    { name: t('books'), icon: Book, href: '#books' },
    { name: t('courses'), icon: GraduationCap, href: '#courses' },
    { name: t('dubbed'), icon: Globe, href: '#dubbed' },
    { name: t('askDrTitle'), icon: MessageCircle, href: '#ask-dr-nawwaf' },
    { name: t('aboutUs'), icon: Info, href: '#about' },
  ];

  return (
    <nav 
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'bg-[#2d1f3d]/95 backdrop-blur-md shadow-lg' : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 py-4">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <div className="flex items-center gap-2">
            <Sparkles className="w-6 h-6 text-pink-400" />
            <span className="text-white font-semibold text-lg">Dr. Nawwaf Alshibli</span>
          </div>

          {/* Navigation Links */}
          <div className="hidden md:flex items-center gap-8">
            {navItems.map((item) => (
              item.isLink ? (
                <Link
                  key={item.name}
                  to={item.href}
                  className="flex items-center gap-2 text-gray-300 hover:text-white transition-colors duration-200 text-sm"
                >
                  <item.icon className="w-4 h-4" />
                  {item.name}
                </Link>
              ) : (
                <a
                  key={item.name}
                  href={item.href}
                  className="flex items-center gap-2 text-gray-300 hover:text-white transition-colors duration-200 text-sm"
                >
                  <item.icon className="w-4 h-4" />
                  {item.name}
                </a>
              )
            ))}
          </div>

          {/* Auth Section */}
          {loading ? (
            <div className="w-24 h-10 bg-gray-700/50 rounded-full animate-pulse"></div>
          ) : isAuthenticated ? (
            <div className="flex items-center gap-3">
              {/* My Books Link */}
              <Link 
                to="/my-books"
                className="flex items-center gap-2 text-gray-300 hover:text-pink-400 transition-colors px-3 py-2 rounded-full hover:bg-white/5"
              >
                <Library className="w-4 h-4" />
                <span className="text-sm hidden lg:block">كتبي</span>
              </Link>
              {/* User Profile */}
              <div className="flex items-center gap-2">
                {user?.picture ? (
                  <img 
                    src={user.picture} 
                    alt={user.name} 
                    className="w-8 h-8 rounded-full border-2 border-pink-500"
                  />
                ) : (
                  <div className="w-8 h-8 rounded-full bg-pink-600 flex items-center justify-center">
                    <User className="w-4 h-4 text-white" />
                  </div>
                )}
                <span className="text-white text-sm hidden lg:block max-w-[100px] truncate">
                  {user?.name?.split(' ')[0]}
                </span>
              </div>
              {/* Logout Button */}
              <Button 
                onClick={logout}
                variant="ghost"
                className="text-gray-300 hover:text-white hover:bg-white/10 px-3 py-2 rounded-full flex items-center gap-2 transition-all duration-200"
              >
                <LogOut className="w-4 h-4" />
              </Button>
            </div>
          ) : (
            <Button 
              onClick={login}
              className="bg-pink-600 hover:bg-pink-700 text-white px-5 py-2 rounded-full flex items-center gap-2 transition-all duration-200"
            >
              <LogIn className="w-4 h-4" />
              {t('login')}
            </Button>
          )}
        </div>
      </div>
    </nav>
  );
};

export default Navbar;
